/**
 * Asset API service — mock fallback until Laravel backend is live.
 */
import api from '@/lib/api';
import { MOCK_ASSETS, addAssetToList, generateInternalBarcode, checkDuplicateIdentifier, resolveAssetByBarcode as mockResolve } from '@/data/mockAssets';
import { createMockAsset } from '@/data/mockData';
import type { Asset, AssetStatus, AssetCondition } from '@/types/models';

const USE_MOCK = !import.meta.env.VITE_API_URL || import.meta.env.VITE_USE_MOCK === 'true';

function delay<T>(val: T, ms = 300): Promise<T> {
  return new Promise((r) => setTimeout(() => r(val), ms));
}

function unwrapCollection<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as T[];
  }
  return [];
}

function unwrapResource<T>(payload: unknown): T | null {
  if (!payload) return null;
  if (typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data)) return obj.data as T;
  }
  return payload as T;
}

export async function fetchAssets(): Promise<Asset[]> {
  if (USE_MOCK) return delay([...MOCK_ASSETS]);
  const res = await api.get('/api/assets');
  return unwrapCollection<Asset>(res.data);
}

export async function fetchAsset(id: string): Promise<Asset | null> {
  if (USE_MOCK) return delay(MOCK_ASSETS.find((a) => a.id === id) ?? null);
  const res = await api.get(`/api/assets/${id}`);
  return unwrapResource<Asset>(res.data);
}

export async function createAsset(payload: Omit<Asset, 'id' | 'internal_barcode' | 'created_at' | 'updated_at'>): Promise<Asset> {
  if (USE_MOCK) {
    const asset = createMockAsset(payload);
    addAssetToList(asset);
    return delay(asset);
  }
  const res = await api.post('/api/assets', payload);
  return unwrapResource<Asset>(res.data) as Asset;
}

export async function updateAssetStatus(id: string, status: AssetStatus): Promise<Asset> {
  if (USE_MOCK) {
    const idx = MOCK_ASSETS.findIndex((a) => a.id === id);
    if (idx >= 0) {
      MOCK_ASSETS[idx] = { ...MOCK_ASSETS[idx], status, updated_at: new Date().toISOString() };
      return delay(MOCK_ASSETS[idx]);
    }
    throw new Error('Asset not found');
  }
  const res = await api.patch(`/api/assets/${id}/status`, { status });
  return unwrapResource<Asset>(res.data) as Asset;
}

export async function updateAsset(id: string, updates: Partial<Asset>): Promise<Asset> {
  if (USE_MOCK) {
    const idx = MOCK_ASSETS.findIndex((a) => a.id === id);
    if (idx >= 0) {
      MOCK_ASSETS[idx] = { ...MOCK_ASSETS[idx], ...updates, updated_at: new Date().toISOString() };
      return delay(MOCK_ASSETS[idx]);
    }
    throw new Error('Asset not found');
  }
  const res = await api.put(`/api/assets/${id}`, updates);
  return unwrapResource<Asset>(res.data) as Asset;
}

export async function resolveAssetByBarcode(barcode: string): Promise<Asset | null> {
  if (USE_MOCK) return mockResolve(barcode);
  const res = await api.get(`/api/assets/resolve/${barcode}`);
  return unwrapResource<Asset>(res.data);
}

export { generateInternalBarcode, checkDuplicateIdentifier };
